import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // customer: null=loading, obj, false=logged out
  const [staff, setStaff] = useState(null);  // admin/barber: null=loading, obj, false=logged out
  const [barbers, setBarbers] = useState([]);
  const [shop, setShop] = useState(null);
  const [settings, setSettings] = useState(null);

  const loadMe = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); } catch { setUser(false); }
  };
  const loadStaff = async () => {
    try { const { data } = await api.get("/auth/staff/me"); setStaff(data); } catch { setStaff(false); }
  };

  useEffect(() => {
    loadMe();
    loadStaff();
    api.get("/barbers").then((r) => setBarbers(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setShop(r.data.shop); setSettings(r.data); }).catch(() => {});
  }, []);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    if (data.csrf_token) api.defaults.headers.common["X-CSRF-Token"] = data.csrf_token;
    if (remember) localStorage.setItem("wn_persistent", "1"); else localStorage.removeItem("wn_persistent");
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.csrf_token) api.defaults.headers.common["X-CSRF-Token"] = data.csrf_token;
    if (payload.remember) localStorage.setItem("wn_persistent", "1"); else localStorage.removeItem("wn_persistent");
    setUser(data.user);
    return data.user;
  };

  const staffLogin = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/staff/login", { email, password, remember });
    if (data.csrf_token) api.defaults.headers.common["X-CSRF-Token"] = data.csrf_token;
    setStaff(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout failed", e); }
    localStorage.removeItem("wn_persistent");
    setUser(false);
  };

  const staffLogout = async () => {
    try { await api.post("/auth/staff/logout"); } catch (e) { console.error("Staff logout failed", e); }
    setStaff(false);
  };

  const refreshUser = async () => { const { data } = await api.get("/auth/me"); setUser(data); };
  const refreshStaff = async () => { const { data } = await api.get("/auth/staff/me"); setStaff(data); };

  const refreshShop = async () => {
    try { const { data } = await api.get("/settings"); setShop(data.shop); setSettings(data); }
    catch (e) { console.error("Failed to refresh settings", e); }
  };

  // Auto sign-out after inactivity — but a customer who chose "Remember me" stays signed in
  // (up to the 30-day cookie). Staff sessions always use the inactivity timeout for security.
  useEffect(() => {
    const persistent = localStorage.getItem("wn_persistent") === "1";
    const customerNeedsTimer = !!user && !persistent;
    const staffNeedsTimer = !!staff;
    if (!customerNeedsTimer && !staffNeedsTimer) return;
    const mins = Number(settings?.session_timeout_minutes) > 0 ? Number(settings.session_timeout_minutes) : 30;
    const ms = mins * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        if (customerNeedsTimer) await logout();
        if (staffNeedsTimer) await staffLogout();
        toast.info("Signed out due to inactivity.");
      }, ms);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, staff, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{ user, setUser, staff, setStaff, login, register, staffLogin, logout, staffLogout,
               refreshUser, refreshStaff, refreshShop, barbers, shop, settings }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
